import { gridSize, gridSizeMdLg12, gridSizeMdLg6, texto } from "@/shared";
import { CustomAutocomplete, CustomAutocompleteArrStringNoForm, CustomTextArea, CustomTextField, CustomTextFieldNoForm } from "@/shared/components/ui";
import { CacheKeyCalendarioENUM } from "@/shared/interfaces";
import useSelectDateTimerPreScheduling, { DepartamentoSelectScheduling } from "@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store";
import { SelectDateTimerSchedulingPyl } from "@/erp/operaciones/agendamiento/shared/components/calendario-preagendamiento-pyl/SelectDateTimerSchedulingPyl";
import { useEffect, useMemo } from "react";
import { ArrowForward, FmdGood, LocationCity, LocationSearching, MyLocation } from "@mui/icons-material";
import { Alert, Box, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Grid, IconButton, InputAdornment, Paper, Stack, SvgIcon, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Contrato, Sector } from "@/shared/interfaces";
import { warmTheme } from "./OrdenServicioTraslado";
import { forwardRef } from "react";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ConceptosTrasladoSection from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/ConceptosTrasladoSection";

/** Celda label/valor para los datos del cliente (Visión 360). */
function DatoCliente({ label, value, sm = 3 }: { label: string; value?: any; sm?: number }) {
  return (
    <Grid item xs={6} sm={sm}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value || "—"}</Typography>
    </Grid>
  );
}

interface TrasladoFormProps {
  contrato: Contrato | null;
  cliente?: any;
  saldoPendiente?: string | number;
  fibraPoints?: string | number;
  descuentoFibra?: number;
  form: any;
  existeCobertura: boolean;
  dataSector: { data: { data: Sector[] } } | undefined;
  isLoadingSector: boolean;
  napsByCity: any[];
  isLoadingNapsByCity: boolean;
  onCoordenadaValida: (value: string, valid: boolean | undefined) => void;
  onSelectSector: (value: any) => void;
  onOpenMapa: () => void;
  onObtenerUbicacionActual: () => void;
}

const TrasladoForm = forwardRef<HTMLDivElement, TrasladoFormProps>(function TrasladoForm({
  contrato,
  form,
  existeCobertura,
  dataSector,
  isLoadingSector,
  napsByCity,
  isLoadingNapsByCity,
  onCoordenadaValida,
  onSelectSector,
  onOpenMapa,
  onObtenerUbicacionActual,
  saldoPendiente,
  fibraPoints,
  cliente,
  descuentoFibra,
}, ref) {

  // form
  const {
    control,
    formState: { errors },
  } = form;

  // Detalle de valores / FibraPoints
  // TODO: DESCUENTO_FIBRA parametrizable desde Catálogos > Administración.
  const DESCUENTO_FIBRA = descuentoFibra ?? 1200;
  const fibra = Number(fibraPoints) || 0;
  // Solo se puede pagar el traslado con FibraPoints si el cliente tiene AL MENOS
  // el descuento requerido (no alcanza con tener > 0).
  const puedeUsarFibra = fibra >= DESCUENTO_FIBRA;
  const trasladoPorFibra = !!form.watch("traslado_por_fibrapoints");

  // Opciones de recaudación (excluyentes: exactamente una debe estar activa).
  const recaudacionTecnico = !!form.watch("recaudacion_tecnico");
  const cargoAFactura = !!form.watch("cargo_a_factura");

  // --- Cálculo del aproximado a pagar por el cliente ---
  // Se recalcula en vivo al escribir el valor o los meses a diferir.
  // El backend cobra el rubro TRASLADOS con `valor` = valor_traslado (sin sumar
  // IVA en ERP) y, si es Cargo a factura, difiere ese total en `meses_pago`
  // cuotas. Con FibraPoints NO hay cobro monetario (es otro método), por eso el
  // resumen solo aplica a Recaudación técnico / Cargo a factura.
  const valorTrasladoRaw = form.watch("valor_traslado");
  const mesesPagoRaw = form.watch("meses_pago");
  const valorNum = Number(String(valorTrasladoRaw ?? "").replace(",", ".")) || 0;
  const mesesNum = cargoAFactura
    ? Math.max(1, Math.floor(Number(mesesPagoRaw) || 1))
    : 1;
  const cuotaMensual = mesesNum > 0 ? valorNum / mesesNum : valorNum;
  const mostrarResumenPago =
    !trasladoPorFibra && (recaudacionTecnico || cargoAFactura) && valorNum > 0;

  // El planificador lee su estado del store zustand (no de props); lo memoizamos
  // con referencia estable para que NO se re-renderice en cada tecla del form
  // (p. ej. al escribir Observaciones) y no "salte" el ícono/horarios.
  const schedulerEl = useMemo(() => <SelectDateTimerSchedulingPyl mensajeBloqueo={texto.mensaje_bloquar_horario} />, []);

  // --- Planificador (Técnico/Fecha/Horas) para agendar el traslado ---
  const getFloat = useSelectDateTimerPreScheduling((s) => s.getFloat);
  const setConfigParams = useSelectDateTimerPreScheduling((s) => s.setConfigParams);
  const setPreventa = useSelectDateTimerPreScheduling((s) => s.setPreventa);
  // Código de zona de la NUEVA ubicación (lo calcula postPoligono al ingresar
  // las coordenadas del destino). Es la zona por la que se filtran las flotas.
  const zonaNueva = form.watch("zona");

  useEffect(() => {
    setConfigParams({
      // Contador visible del bloqueo: 1 minuto, igual que el bloqueo real
      // (TIEMPO_LIMITE_PYL = 00:01) y que la pantalla de Operaciones.
      time: 60 * 1000,
      tiempo: CacheKeyCalendarioENUM.TIEMPO_LIMITE_PYL,
      motivo: CacheKeyCalendarioENUM.MENSAJE_BLOQUEO_PYL,
      departamento: DepartamentoSelectScheduling.PYL,
    });
    if ((contrato as any)?.id_contrato) setPreventa((contrato as any).id_contrato);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (zonaNueva) {
      getFloat({ estado: true, zonas: zonaNueva, page_size: 100 } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonaNueva]);

  return (
    <Box ref={ref} sx={{ mt: 3 }}>
      <Divider sx={{ my: 2 }}>
        <Chip
          label={`Información de Traslado ${contrato?.codigo ? `(${contrato.codigo})` : ''}`}
          size="small"
          sx={{
            bgcolor: alpha(warmTheme.palette.primary.main, 0.1),
            color: "primary.dark",
            fontWeight: 600,
          }}
        />
      </Divider>

      {/* Current Client Info */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(warmTheme.palette.secondary.main, 0.15),
          borderRadius: 2,
          mb: 3,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.secondary.main, 0.3),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <LocationCity
            sx={{ color: "primary.main", fontSize: 20 }}
          />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="primary.dark"
          >
            {`Ubicación Actual del Servicio`}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {contrato && (
            <Grid container spacing={3}>
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Cordenadas"}
                defaultValue={contrato.coordenadas ?? contrato.coordenadas ?? ''}
                size={gridSizeMdLg12}
              />
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Dirección"}
                defaultValue={contrato.direccion}
                size={gridSizeMdLg12}
              />
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Provincia"}
                defaultValue={contrato?.provincia ?? contrato.provincia_data?.nombre ?? ''}
                size={gridSizeMdLg6}
              />
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Ciudad"}
                defaultValue={contrato.ciudad ?? contrato.ciudad_data?.nombre ?? ''}
                size={gridSizeMdLg6}
              />
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Parroquia"}
                defaultValue={contrato.sector ?? contrato.sector_data?.nombre ?? ''}
                size={gridSizeMdLg6}
              />
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Zona"}
                defaultValue={contrato.zona ?? contrato.zona_data?.nombre ?? ''}
                size={gridSizeMdLg6}
              />
              <CustomTextFieldNoForm
                required={false}
                disabled={true}
                label={"Sector"}
                defaultValue={contrato.sector_data?.nombre}
                size={gridSizeMdLg6}
              />
            </Grid>
          )}
        </Box>
      </Paper>

      {/* Datos del Cliente (Visión 360) */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(warmTheme.palette.primary.main, 0.04),
          borderRadius: 2,
          mb: 3,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.primary.main, 0.2),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <StorefrontIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
            Datos del Cliente
          </Typography>
        </Box>

        {/* Datos personales */}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 1, letterSpacing: 0.5 }}>
          DATOS PERSONALES
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <DatoCliente label="Tipo Documento" value={(cliente as any)?.tipo_identificacion} />
          <DatoCliente label="Identificación" value={(cliente as any)?.identificacion} />
          <DatoCliente label="Nombre Cliente" value={(cliente as any)?.razon_social} sm={6} />
          {(cliente as any)?.fecha_nacimiento && (
            <DatoCliente label="Fecha de Nacimiento" value={(cliente as any)?.fecha_nacimiento} />
          )}
          <DatoCliente label="Correo Electrónico" value={(cliente as any)?.email} sm={6} />
          {(cliente as any)?.celular && (
            <DatoCliente label="Teléfono Móvil" value={(cliente as any)?.celular} />
          )}
          {(cliente as any)?.celular_2 && (
            <DatoCliente label="Teléfono 2" value={(cliente as any)?.celular_2} />
          )}
          {(contrato as any)?.persona_referencia_nombre && (
            <DatoCliente label="Persona de referencia" value={(contrato as any)?.persona_referencia_nombre} />
          )}
          {((contrato as any)?.persona_referencia_celular || (contrato as any)?.contacto_telefono) && (
            <DatoCliente
              label="Teléfono referencia"
              value={(contrato as any)?.persona_referencia_celular || (contrato as any)?.contacto_telefono}
            />
          )}
          {(contrato as any)?.persona_referencia_parentesco && (
            <DatoCliente label="Parentesco referencia" value={(contrato as any)?.persona_referencia_parentesco} />
          )}
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* Datos comerciales */}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 1, letterSpacing: 0.5 }}>
          DATOS COMERCIALES
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <DatoCliente label="Plan" value={(contrato as any)?.plan} />
          <DatoCliente label="Velocidad" value={(contrato as any)?.velocidad ? `${(contrato as any).velocidad} Mbps` : undefined} />
          <DatoCliente label="Costo Mensual" value={(contrato as any)?.valor_plan ? `$${(contrato as any).valor_plan}` : undefined} />
          <DatoCliente label="Tipo Servicio" value={(contrato as any)?.tipo_servicio} />
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* Promociones y Pago */}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 1, letterSpacing: 0.5 }}>
          PROMOCIONES Y PAGO
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Promociones</Typography>
            {(contrato as any)?.promociones ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                {String((contrato as any).promociones).split(",").map((p: string, i: number) => (
                  <Chip key={i} label={p.trim()} size="small" variant="outlined" color="primary" />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" fontWeight={600}>—</Typography>
            )}
          </Grid>
          <DatoCliente label="Forma de Pago" value={(contrato as any)?.forma_pago} sm={6} />
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Saldo Pendiente</Typography>
            <Typography variant="h6" fontWeight={800} color={Number(saldoPendiente) > 0 ? "error.main" : "success.main"}>
              ${saldoPendiente ?? "0"}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary" display="block">Fibra Points</Typography>
            <Typography variant="h6" fontWeight={800} color="primary.main">
              {fibraPoints ?? "—"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Arrow indicator */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          my: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: alpha(warmTheme.palette.primary.main, 0.1),
            px: 2,
            py: 0.5,
            borderRadius: 10,
          }}
        >
          <ArrowForward sx={{ color: "primary.main" }} />
          <Typography
            variant="caption"
            color="primary.dark"
            fontWeight={600}
          >
            Nueva ubicación
          </Typography>
        </Box>
      </Box>

      {/* New Location Form */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(warmTheme.palette.primary.main, 0.05),
          borderRadius: 2,
          border: "2px dashed",
          borderColor: alpha(warmTheme.palette.primary.main, 0.3),
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="primary.dark"
          sx={{ mb: 3 }}
        >
          📍 Nueva Ubicación
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              alignContent: "center",
            }}
          >
            <CustomTextField
              type="coordenada"
              control={control}
              name="coordenadas"
              defaultValue={form.getValues().coordenadas}
              size={{
                xs: 10,
                sm: 10,
                md: 10,
                lg: 10,
              }}
              label={texto.coordenadas}
              error={errors.coordenadas}
              startAdornment={
                <InputAdornment position="start">
                  <SvgIcon fontSize="small">
                    <MyLocation />
                  </SvgIcon>
                </InputAdornment>
              }
              helperText={errors.coordenada?.message}
              onChangeValue={(value, valid) =>
                onCoordenadaValida(value, valid)
              }
            />

            <Grid item xs={1} alignItems={"end"}>
              <Tooltip title="Abrir Mapa" placement="top">
                <IconButton onClick={onOpenMapa} size="small">
                  <FmdGood color="info" />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={1} alignItems={"end"}>
              <Tooltip title="Obtener ubicación actual" placement="top">
                <IconButton onClick={onObtenerUbicacionActual} size="small">
                  <LocationSearching color="info" />
                </IconButton>
              </Tooltip>
            </Grid>

            {!existeCobertura && (
              <Grid item {...gridSizeMdLg12}>
                <Alert variant="outlined" severity="warning">
                  Sin cobertura para las coordenas ingresadas
                </Alert>
              </Grid>
            )}

            {existeCobertura && (
              <>
                <CustomAutocomplete<Sector>
                  size={gridSize}
                  label="Seleccionar Parroquia"
                  name="sector"
                  options={dataSector?.data.data || []}
                  valueKey="nombre"
                  actualValueKey="codigo"
                  defaultValue={form.getValues().sector}
                  isLoadingData={isLoadingSector}
                  control={control}
                  error={errors.sector}
                  helperText={errors.sector?.message}
                  onChangeRawValue={onSelectSector}
                />

                <CustomTextField
                  type="direccion"
                  control={control}
                  name="direccion"
                  defaultValue={form.getValues().direccion || ""}
                  size={gridSize}
                  label={texto.direccion}
                  error={errors.direccion}
                  helperText={errors.direccion?.message}
                />

                <CustomTextFieldNoForm
                  required={false}
                  disabled={true}
                  label={"Provincia"}
                  defaultValue={form.getValues().provincia_nombre}
                  value={form.getValues().provincia_nombre}
                  size={gridSizeMdLg6}
                />
                <CustomTextFieldNoForm
                  required={false}
                  disabled={true}
                  label={"Ciudad"}
                  defaultValue={form.getValues().ciudad_nombre}
                  value={form.getValues().ciudad_nombre}
                  size={gridSizeMdLg6}
                />

                <CustomTextFieldNoForm
                  required={false}
                  disabled={true}
                  label={"Zona"}
                  defaultValue={form.getValues().zona_nombre}
                  value={form.getValues().zona_nombre}
                  size={gridSizeMdLg6}
                />

                {isLoadingNapsByCity && (
                  <Grid item {...gridSizeMdLg12} textAlign={"center"}>
                    <CircularProgress />
                  </Grid>
                )}
                {napsByCity.length == 0 && !isLoadingNapsByCity && (
                  <Grid item {...gridSizeMdLg12}>
                    <Alert variant="outlined" severity="warning">
                      No hay cajas disponibles para las coordendas asignadas
                    </Alert>
                  </Grid>
                )}
                {napsByCity.length != 0 && !isLoadingNapsByCity && (
                  <Grid item {...gridSizeMdLg12}>
                    <Alert variant="outlined" severity="success">
                      Cajas disponibles para las coordendas asignadas
                    </Alert>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Box>
      </Paper>

      {/* Detalle de Valores */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 3,
          bgcolor: alpha(warmTheme.palette.secondary.main, 0.1),
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.secondary.main, 0.3),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 2 }}>
          Detalle de Valores
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          <CustomAutocompleteArrStringNoForm
            label="Categoría"
            options={["SOPORTE TECNICO"]}
            defaultValue={"SOPORTE TECNICO"}
            disableClearable
            size={gridSizeMdLg6}
            sizeAutocomplete="small"
            onChangeValue={() => {}}
          />
          <CustomAutocompleteArrStringNoForm
            label="Subcategoría"
            options={["REQUERIMIENTO CLIENTE"]}
            defaultValue={"REQUERIMIENTO CLIENTE"}
            disableClearable
            size={gridSizeMdLg6}
            sizeAutocomplete="small"
            onChangeValue={() => {}}
          />
          <CustomAutocompleteArrStringNoForm
            label="Causal"
            options={["TRASLADO"]}
            defaultValue={"TRASLADO"}
            disableClearable
            size={gridSizeMdLg6}
            sizeAutocomplete="small"
            onChangeValue={() => {}}
          />
          <CustomTextField
            name="valor_traslado"
            label="Valor Traslado"
            control={control}
            type="moneda"
            defaultValue={form.getValues().valor_traslado ?? "23.00"}
            size={gridSizeMdLg6}
            disabled={trasladoPorFibra}
            startAdornment={
              <InputAdornment position="start">$</InputAdornment>
            }
            error={errors.valor_traslado}
            helperText={errors.valor_traslado?.message}
          />

          <Grid item {...gridSizeMdLg12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mt: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={trasladoPorFibra}
                    disabled={!puedeUsarFibra}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      form.setValue("traslado_por_fibrapoints", checked);
                      // Con FibraPoints el valor es 0 y no editable; sin él, $23.00.
                      form.setValue("valor_traslado", checked ? "0" : "23.00");
                      // Con FibraPoints NO aplica recaudación: limpiar ambas opciones
                      // (quedan deshabilitadas y no se validan).
                      if (checked) {
                        form.setValue("recaudacion_tecnico", false);
                        form.setValue("cargo_a_factura", false);
                      }
                    }}
                  />
                }
                label="¿Traslado por FibraPoints?"
              />
              <Chip
                label={`FibraPoints disponibles: ${fibra}`}
                size="small"
                color={puedeUsarFibra ? "primary" : "default"}
                variant="outlined"
              />
              {!puedeUsarFibra && (
                <Typography variant="caption" color="error.main" fontWeight={600}>
                  {fibra <= 0
                    ? "Contrato sin FibraPoints disponibles"
                    : `FibraPoints insuficientes (necesita ${DESCUENTO_FIBRA})`}
                </Typography>
              )}
              {trasladoPorFibra && (
                <Typography variant="caption" color="warning.main" fontWeight={700}>
                  Se descontarán {DESCUENTO_FIBRA} FibraPoints
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Opciones de recaudación: excluyentes, exactamente una activa */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Opciones de recaudación
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={recaudacionTecnico}
                    disabled={trasladoPorFibra}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      form.setValue("recaudacion_tecnico", checked);
                      // Excluyentes: al activar una se desactiva la otra.
                      if (checked) form.setValue("cargo_a_factura", false);
                    }}
                  />
                }
                label="¿Recaudación técnico?"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cargoAFactura}
                    disabled={trasladoPorFibra}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      form.setValue("cargo_a_factura", checked);
                      if (checked) form.setValue("recaudacion_tecnico", false);
                    }}
                  />
                }
                label="¿Cargo a factura?"
              />
              {/* Con FibraPoints la recaudación no aplica: no se muestra el error. */}
              {!trasladoPorFibra && !recaudacionTecnico && !cargoAFactura && (
                <Typography variant="caption" color="error.main" fontWeight={600}>
                  Seleccioná una opción de recaudación.
                </Typography>
              )}
              {trasladoPorFibra && (
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  Pago por FibraPoints: la recaudación no aplica.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Meses a diferir: SOLO cuando es Cargo a factura. Enteros; default 1
              (1 = una cuota; más de 1 = diferido). Va en meses_pago del cobro. */}
          {cargoAFactura && (
            <CustomTextField
              name="meses_pago"
              label="Meses a diferir"
              control={control}
              type="entero"
              defaultValue={form.getValues().meses_pago ?? "1"}
              required={false}
              error={errors.meses_pago}
              helperText={
                (errors.meses_pago?.message as string) ||
                "1 = una cuota; más de 1 = diferido"
              }
              size={gridSizeMdLg6}
            />
          )}

          {/* Aproximado a pagar por el cliente. No se muestra con FibraPoints
              (es otro método de pago, no genera cobro monetario). */}
          {mostrarResumenPago && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mt: 1,
                  borderRadius: 2,
                  bgcolor: alpha(warmTheme.palette.primary.main, 0.06),
                  borderColor: alpha(warmTheme.palette.primary.main, 0.3),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
                    Aproximado a pagar por el cliente
                  </Typography>
                </Stack>

                {cargoAFactura ? (
                  <Stack spacing={0.75}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">
                        Valor del traslado
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${valorNum.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">
                        Meses a diferir
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {mesesNum} {mesesNum === 1 ? "mes" : "meses"}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        Cuota mensual aprox.
                      </Typography>
                      <Chip
                        label={`$${cuotaMensual.toFixed(2)} / mes`}
                        color="primary"
                        sx={{ fontWeight: 800 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {mesesNum === 1
                        ? "Se cargará en 1 cuota en la próxima factura."
                        : `Se difiere en ${mesesNum} cuotas de $${cuotaMensual.toFixed(
                            2
                          )} c/u (total $${valorNum.toFixed(2)}).`}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={0.75}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        Total a recaudar por el técnico
                      </Typography>
                      <Chip
                        label={`$${valorNum.toFixed(2)}`}
                        color="primary"
                        sx={{ fontWeight: 800 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      El técnico recauda el valor completo en un solo pago en sitio.
                    </Typography>
                  </Stack>
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontStyle="italic"
                  sx={{ display: "block", mt: 1 }}
                >
                  * Cálculo referencial. El valor final puede variar según impuestos o
                  ajustes de facturación.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Otros conceptos del traslado (catálogo + métodos de pago de traslados).
          Viajan al micro con la orden y llegan a Django (agendamiento.observaciones);
          el técnico completa series en sitio y se cobran a Softnet al finalizar. */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 3,
          bgcolor: alpha(warmTheme.palette.secondary.main, 0.06),
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.secondary.main, 0.3),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 2 }}>
          Otros conceptos del traslado
        </Typography>
        <ConceptosTrasladoSection
          conceptos={form.watch("otros_conceptos") || []}
          onChangeConceptos={(v) => form.setValue("otros_conceptos", v, { shouldDirty: true })}
          hidePuntosRed
          title="Conceptos y método de pago"
          subtitle="Equipos/servicios adicionales que se cobrarán con el traslado. El técnico elegirá la serie entregada en sitio; los puntos de red (cable UTP) los registra el técnico."
        />
      </Paper>

      {/* Confirmación del traslado en sitio (planificador Técnico/Fecha/Horas) */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 3,
          bgcolor: alpha(warmTheme.palette.primary.main, 0.04),
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.primary.main, 0.2),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 2 }}>
          Confirmación del traslado en sitio
        </Typography>

        {/* El planificador (técnico/fecha/horas) SOLO se muestra cuando ya hay
            zona de la nueva ubicación; si no, quedaría cargando técnicos vacío. */}
        {!zonaNueva ? (
          <Alert severity="info" variant="outlined">
            Ingresá primero las coordenadas de la <b>nueva ubicación</b> para
            cargar los técnicos y horarios disponibles de esa zona.
          </Alert>
        ) : (
          <>
            {schedulerEl}

            <Box sx={{ mt: 2 }}>
              <CustomTextArea
                label="Observaciones para el técnico"
                name="observaciones"
                control={control}
                defaultValue={form.getValues().observaciones || ""}
                error={errors.observaciones}
                helperText={errors.observaciones?.message}
                required={false}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
});

export default TrasladoForm;
